import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Alert, FormCheck } from 'react-bootstrap';
import MicrosoftSSO from '../sso/MicrosoftSSO';
import GoogleSSO from '../sso/GoogleSSO';
import AlertNotification from '../../components/AlertNotification';
import { FaRocket, FaShieldAlt, FaFileAlt, FaWrench } from 'react-icons/fa';
import PasswordField from '../components/PasswordField';
import FieldError from '../components/FieldError';
import './Login.css';
import { resetSessionReduxState } from '../../redux/sessionReset';
import {
  clearRememberedIdentifier,
  loadRememberedIdentifier,
  persistSessionToken,
  saveRememberedIdentifier,
} from '../storage';
import { isValidEmail, PASSWORD_MISMATCH_MESSAGE, validateRegisterFields, validateSignInFields } from '../validation';
import {
  apiMessage,
  forgotPasswordRequest,
  googleSsoRequest,
  loginRequest,
  microsoftSsoRequest,
  registerRequest,
} from '../api';

// Register-form fields in on-screen order, mapped to their input ids — used to put the
// cursor on the first invalid field after a failed submit.
const REGISTER_FIELD_IDS = {
  firstName: 'firstName',
  email: 'identifier',
  password: 'password',
  confirmPassword: 'confirmPassword',
};

const Login = ({ setIsAuthenticated }) => {
  const [mode, setMode] = useState('signin');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Each password field has its own show/hide state, so revealing one never reveals the other.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  // Client-side register validation, keyed by field so each message sits next to its input.
  // `error` above stays the banner for form-level / server errors.
  const [fieldErrors, setFieldErrors] = useState({});
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', variant: 'success' });
  const navigate = useNavigate();
  const closeToast = () => setToast((t) => ({ ...t, message: '' }));

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setInfo('');
    setFieldErrors({});
    // A revealed password should not stay revealed on the next form.
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  // Drop a field's message as soon as the user edits it, so it never describes stale input.
  const clearFieldErrors = (...keys) => {
    setFieldErrors((prev) => {
      if (!keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const passwordMismatch = fieldErrors.confirmPassword === PASSWORD_MISMATCH_MESSAGE;

  useEffect(() => {
    const saved = loadRememberedIdentifier();
    setIdentifier(saved.identifier || '');
    setRememberMe(saved.rememberMe || false);
  }, []);

  const completeLogin = (token) => {
    persistSessionToken(token, rememberMe);
    if (rememberMe) {
      saveRememberedIdentifier(identifier);
    } else {
      clearRememberedIdentifier();
    }
    resetSessionReduxState();
    setIsAuthenticated(true);
    navigate('/home');
  };

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    if (checked) {
      saveRememberedIdentifier(identifier);
    } else {
      clearRememberedIdentifier();
    }
  };

  const handleIdentifierChange = (e) => {
    const value = e.target.value;
    setIdentifier(value);
    clearFieldErrors('email');
    if (rememberMe) {
      saveRememberedIdentifier(value);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    // Sign-in is by registered email address only; the API enforces the same rule.
    const errors = validateSignInFields({ email: identifier, password });
    const firstInvalid = ['email', 'password'].find((field) => errors[field]);
    if (firstInvalid) {
      setFieldErrors(errors);
      document.getElementById(REGISTER_FIELD_IDS[firstInvalid])?.focus();
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      const response = await loginRequest(identifier.trim(), password);
      completeLogin(response.data.token);
    } catch (err) {
      setError(apiMessage(err, 'Login failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    const errors = validateRegisterFields({
      firstName,
      email: identifier,
      password,
      confirmPassword,
    });
    const firstInvalid = Object.keys(REGISTER_FIELD_IDS).find((field) => errors[field]);
    if (firstInvalid) {
      setFieldErrors(errors);
      document.getElementById(REGISTER_FIELD_IDS[firstInvalid])?.focus();
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      await registerRequest({
        email: identifier.trim(),
        password,
        confirmPassword,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      // Deliberately not completeLogin() here — the account is created, but the user
      // should explicitly sign in with it rather than being silently logged straight
      // into the app with no confirmation anything happened (see bug report: no
      // success message, no indication of success, no prompt to sign in).
      setFirstName('');
      setLastName('');
      setPassword('');
      setConfirmPassword('');
      switchMode('signin');
      setToast({
        message: 'Account created successfully. Please sign in — check your inbox for a confirmation email too.',
        variant: 'success',
      });
    } catch (err) {
      setError(apiMessage(err, 'Registration failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!identifier.trim()) {
      setError('Email is required');
      return;
    }
    if (!isValidEmail(identifier)) {
      setError('Enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const response = await forgotPasswordRequest(identifier.trim());
      setInfo(response.data?.message || 'If an account exists for that email, a reset link has been sent.');
    } catch (err) {
      setError(apiMessage(err, 'Unable to process that request. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLoginSuccess = async (userInfo) => {
    try {
      const response = await microsoftSsoRequest({
        email: userInfo.email,
        name: userInfo.name,
        microsoftId: userInfo.id,
        accessToken: userInfo.accessToken,
        idToken: userInfo.idToken,
      });

      const { token } = response.data;
      persistSessionToken(token, rememberMe);
      const store = rememberMe ? localStorage : sessionStorage;
      store.setItem('microsoftToken', userInfo.accessToken);
      store.setItem('microsoftUserInfo', JSON.stringify(userInfo));
      if (rememberMe) saveRememberedIdentifier(userInfo.email || identifier);
      else clearRememberedIdentifier();
      resetSessionReduxState();
      setIsAuthenticated(true);
      navigate('/home');
    } catch (err) {
      setError(apiMessage(err, 'Microsoft SSO login failed. Please try again.'));
    }
  };

  const handleMicrosoftLoginError = (error) => {
    setError('Microsoft SSO login failed. Please try again.');
    console.error('Microsoft SSO error:', error);
  };

  const handleGoogleLoginSuccess = async (userInfo) => {
    try {
      const response = await googleSsoRequest({
        email: userInfo.email,
        name: userInfo.name,
        googleId: userInfo.id,
        accessToken: userInfo.accessToken,
        idToken: userInfo.idToken,
        picture: userInfo.picture,
      });

      const { token } = response.data;
      persistSessionToken(token, rememberMe);
      const store = rememberMe ? localStorage : sessionStorage;
      store.setItem('googleToken', userInfo.accessToken);
      store.setItem('googleUserInfo', JSON.stringify(userInfo));
      if (rememberMe) saveRememberedIdentifier(userInfo.email || identifier);
      else clearRememberedIdentifier();
      resetSessionReduxState();
      setIsAuthenticated(true);
      navigate('/home');
    } catch (err) {
      setError(apiMessage(err, 'Google SSO login failed. Please try again.'));
    }
  };

  const handleGoogleLoginError = (error) => {
    setError('Google SSO login failed. Please try again.');
    console.error('Google SSO error:', error);
  };

  const features = [
    {
      icon: FaRocket,
      title: 'AI-Powered Solutions',
      description: 'Leverage AI to turn requirements, code, and UI sources into a grounded specification and test suite.'
    },
    {
      icon: FaShieldAlt,
      title: 'Secure & Reliable',
      description: 'Enterprise-grade security with role-based access control and secure data handling for your sensitive projects.'
    },
    {
      icon: FaFileAlt,
      title: 'Faster Development',
      description: 'Move from intake to a confirmed spec and generated tests without switching tools.'
    },
    {
      icon: FaWrench,
      title: 'Comprehensive Tools',
      description: 'Requirement intake, spec review, and test-suite generation in one Automate workflow.'
    }
  ];

  const titles = { signin: 'Sign In', register: 'Create Account', forgot: 'Reset Password' };
  const togglePassword = () => setShowPassword((v) => !v);
  const toggleConfirmPassword = () => setShowConfirmPassword((v) => !v);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    // A "do not match" message is about the pair, so editing either field retires it.
    clearFieldErrors('password', ...(passwordMismatch ? ['confirmPassword'] : []));
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    clearFieldErrors('confirmPassword');
  };

  return (
    <div className="login-page">
      <Container fluid className="login-container">
        <Row className="g-0 login-row">
          <Col lg={5} className="features-panel order-2 order-lg-0">
            <div className="features-content">
              <div className="features-logo">
                <div className="logo-container">
                  <img src="/logonew.png" alt="Continuum Logo" className="logo-image" />
                </div>
              </div>
              <div className="features-header">
                <h1 className="features-title">Why Choose Continuum?</h1>
                <div className="features-divider"></div>
              </div>
              <div className="features-list">
                {features.map((feature, index) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={index} className="feature-item">
                      <div className="feature-icon">
                        <IconComponent style={{ color: '#ffffff' }} />
                      </div>
                      <div className="feature-content">
                        <h3 className="feature-title">{feature.title}</h3>
                        <p className="feature-description">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Col>

          <Col lg={7} className={`signin-panel order-1 order-lg-0 ${mode === 'register' ? 'auth-tall' : ''}`}>
            <div className="signin-content">
              <h2 className="signin-title">{titles[mode]}</h2>

              <Form
                onSubmit={mode === 'register' ? handleRegister : mode === 'forgot' ? handleForgot : handleSignIn}
                className="signin-form"
                // The app validates every field itself and shows the message under the field; the
                // browser's own pop-up would pre-empt that (type="email" blocks submit on its own).
                noValidate
              >
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                {info && <Alert variant="success" className="mb-3">{info}</Alert>}

                {mode === 'register' && (
                  <>
                    <Form.Group className="mb-3" controlId="firstName">
                      <Form.Label className="form-label">
                        First name
                        <span className="required-mark" aria-hidden="true">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={firstName}
                        onChange={(e) => { setFirstName(e.target.value); clearFieldErrors('firstName'); }}
                        required
                        className="form-input"
                        aria-invalid={fieldErrors.firstName ? true : undefined}
                        aria-describedby={fieldErrors.firstName ? 'firstName-error' : undefined}
                      />
                      <FieldError id="firstName-error">{fieldErrors.firstName}</FieldError>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Label className="form-label">Last name</Form.Label>
                      <Form.Control
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="form-input"
                      />
                    </Form.Group>
                  </>
                )}

                <Form.Group className="mb-3" controlId="identifier">
                  <Form.Label className="form-label">
                    Email
                    <span className="required-mark" aria-hidden="true">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    value={identifier}
                    onChange={handleIdentifierChange}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    className="form-input"
                    aria-invalid={fieldErrors.email ? true : undefined}
                    aria-describedby={fieldErrors.email ? 'identifier-error' : undefined}
                  />
                  <FieldError id="identifier-error">{fieldErrors.email}</FieldError>
                </Form.Group>

                {mode === 'signin' && (
                  <PasswordField
                    id="password"
                    label="Password"
                    value={password}
                    onChange={handlePasswordChange}
                    show={showPassword}
                    onToggleShow={togglePassword}
                    error={fieldErrors.password}
                  />
                )}
                {mode === 'register' && (
                  <>
                    {/* On a mismatch both fields are highlighted, but the message belongs to
                        Confirm password; Password points at it for assistive tech. */}
                    <PasswordField
                      id="password"
                      label="Password"
                      value={password}
                      onChange={handlePasswordChange}
                      show={showPassword}
                      onToggleShow={togglePassword}
                      error={fieldErrors.password}
                      invalid={Boolean(fieldErrors.password) || passwordMismatch}
                      describedBy={passwordMismatch ? 'confirmPassword-error' : undefined}
                    />
                    <PasswordField
                      id="confirmPassword"
                      label="Confirm password"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      show={showConfirmPassword}
                      onToggleShow={toggleConfirmPassword}
                      error={fieldErrors.confirmPassword}
                    />
                  </>
                )}

                {mode === 'signin' && (
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <FormCheck
                      type="checkbox"
                      id="rememberMe"
                      label="Remember Me"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                      className="remember-checkbox"
                    />
                    <button
                      type="button"
                      className="forgot-password-link"
                      onClick={() => switchMode('forgot')}
                    >
                      Forgot password?
                    </button>
                  </div>
                )}

                <button type="submit" className="signin-button" disabled={loading}>
                  {loading ? 'PLEASE WAIT…' : mode === 'register' ? 'CREATE ACCOUNT' : mode === 'forgot' ? 'SEND RESET LINK' : 'SIGN IN'}
                  {!loading && <span className="button-arrow">→</span>}
                </button>
              </Form>

              {mode === 'signin' && (
                <>
                  <div className="or-divider">
                    <div className="or-line"></div>
                    <span className="or-text">OR</span>
                    <div className="or-line"></div>
                  </div>
                  <div className="sso-buttons">
                    <GoogleSSO
                      onLoginSuccess={handleGoogleLoginSuccess}
                      onLoginError={handleGoogleLoginError}
                    />
                    <MicrosoftSSO
                      onLoginSuccess={handleMicrosoftLoginSuccess}
                      onLoginError={handleMicrosoftLoginError}
                    />
                  </div>
                  <div className="create-account-section">
                    <p className="create-account-text">
                      Don&apos;t have an account?{' '}
                      <button
                        type="button"
                        className="create-account-link"
                        onClick={() => { switchMode('register'); setPassword(''); }}
                      >
                        Create account
                      </button>
                    </p>
                  </div>
                </>
              )}

              {mode !== 'signin' && (
                <div className="create-account-section">
                  <p className="create-account-text">
                    <button
                      type="button"
                      className="create-account-link"
                      onClick={() => switchMode('signin')}
                    >
                      Back to sign in
                    </button>
                  </p>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
      <AlertNotification message={toast.message} variant={toast.variant} onClose={closeToast} />
    </div>
  );
};

export default Login;
